// Tahiti authentication service.
//
// Talks to the backend's Tahiti auth proxy (packages/backend/src/routes/
// tahiti-auth-proxy.ts), which forwards to whichever Tahiti host the live
// `tahiti-environment` flag currently selects. The session token is
// persisted in localStorage under `tahiti_token`. Vendored from
// words-interface's lib/auth-service.ts — same Tahiti backend, same login
// page, kept in sync deliberately.

import { z } from 'zod';
import { API_BASE_URL } from './api-base';

const TOKEN_KEY = 'tahiti_token';

/** The backend's Tahiti auth proxy — see routes/tahiti-auth-proxy.ts. */
const AUTH_BASE = `${API_BASE_URL}/auth`;

// ── Error ───────────────────────────────────────────────────────────────────

/** Error thrown by the auth service, carrying a machine-readable code. */
export class AuthError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/** True when the error means the account exists but its e-mail is unverified. */
export function isEmailNotVerified(error: unknown): boolean {
  return error instanceof AuthError && error.code === 'EMAIL_NOT_VERIFIED';
}

// ── Token helpers ─────────────────────────────────────────────────────────────

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // localStorage may be unavailable (private mode, SSR). Treat as no token.
    return null;
  }
}

function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Non-fatal: the session simply won't survive a reload.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Non-fatal.
  }
}

// ── Response schemas ──────────────────────────────────────────────────────────

const loginResponseSchema = z.object({
  success: z.boolean(),
  token: z.string().optional(),
  user: z.unknown().optional(),
  code: z.string().optional(),
  message: z.string().optional(),
});

const statusResponseSchema = z.object({
  authenticated: z.boolean(),
  user: z.unknown().optional(),
});

const errorBodySchema = z.object({
  code: z.string().optional(),
  error: z.string().optional(),
  message: z.string().optional(),
});

// ── API ─────────────────────────────────────────────────────────────────────

export interface LoginResult {
  user: unknown;
}

/**
 * Authenticate against Tahiti. On success stores the session token and returns
 * the user. Throws {@link AuthError} on failure; a `403` / `EMAIL_NOT_VERIFIED`
 * response yields an `AuthError` with code `EMAIL_NOT_VERIFIED` so the form can
 * surface the dedicated message.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  const raw: unknown = await response.json().catch(() => ({}));

  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(raw);
    const code =
      response.status === 403
        ? 'EMAIL_NOT_VERIFIED'
        : (parsed.success && (parsed.data.code ?? parsed.data.error)) || 'AUTH_ERROR';
    const message = (parsed.success ? parsed.data.message : undefined) ?? 'Authentication failed.';
    throw new AuthError(code, message, response.status);
  }

  const body = loginResponseSchema.parse(raw);

  if (body.success === false || !body.token) {
    const code = body.code ?? 'AUTH_ERROR';
    throw new AuthError(
      code === 'EMAIL_NOT_VERIFIED' ? 'EMAIL_NOT_VERIFIED' : code,
      body.message ?? 'Authentication failed.',
      response.status,
    );
  }

  setToken(body.token);
  return { user: body.user };
}

/** End the Tahiti session and clear the local token (always clears, even on error). */
export async function logout(): Promise<void> {
  try {
    await fetch(`${AUTH_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearToken();
  }
}

export interface AuthStatus {
  authenticated: boolean;
  user?: unknown;
}

/** Ask the backend whether the current session is authenticated. */
export async function checkAuthStatus(): Promise<AuthStatus> {
  const token = getToken();
  const response = await fetch(`${AUTH_BASE}/status`, {
    method: 'GET',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    return { authenticated: false };
  }

  const raw: unknown = await response.json().catch(() => ({}));
  const parsed = statusResponseSchema.safeParse(raw);
  if (!parsed.success) {
    return { authenticated: false };
  }
  return parsed.data.user !== undefined
    ? { authenticated: parsed.data.authenticated, user: parsed.data.user }
    : { authenticated: parsed.data.authenticated };
}
